import { Popup as SemanticPopup, PopupProps } from 'semantic-ui-react';
import React, { SyntheticEvent } from 'react';
import { InfoIcon } from '$/images/FontAwesomeIcons';
import { css } from '@emotion/react';
import tw from 'twin.macro';
import Button from './Button';
import { useThemeContext } from './ThemeContext';

export type PopupPosition =
  | 'top left'
  | 'top right'
  | 'bottom right'
  | 'bottom left'
  | 'right center'
  | 'left center'
  | 'top center'
  | 'bottom center';

export const Popup: React.FC<PopupProps & { classNameIcon?: string; classNamePopup?: string }> = ({
  trigger,
  small,
  className,
  classNameIcon,
  classNamePopup,
  ...props
}) => {
  return (
    <div
      className={className}
      css={css`
        margin-left: 0.5rem;
      `}
    >
      <SemanticPopup
        size='small'
        css={css`
          ${tw`font-body`};
          border-radius: 8px;
          padding: 1rem;
          .header + .content {
            padding-top: 0 !important;
          }
          p {
            padding-bottom: 1rem;
            margin-bottom: 0;
          }
          a {
            color: #d0d0d0;
            text-decoration: underline;

            &:hover {
              color: #e0e0e0;
              text-decoration: underline;
            }
          }
          ul {
            list-style: disc;
            margin-left: 2rem;
          }
        `}
        on={['hover', 'click']}
        closeOnEscape
        position='top center'
        hoverable
        inverted
        wide={!small}
        header=''
        trigger={
          trigger || (
            <div className={classNameIcon}>
              <InfoIcon
                css={css`
                  ${tw`h-1e text-color-brand cursor-pointer`}
                `}
              />
            </div>
          )
        }
        className={classNamePopup}
        {...props}
      />
    </div>
  );
};

export const ConfirmPopup: React.FC<PopupProps & { onClick: React.MouseEventHandler<Element> }> = ({
  small,
  onClick,
  ...props
}) => {
  const etheme = useThemeContext();

  return (
    <SemanticPopup
      size='small'
      css={css`
        border-radius: 8px;
        padding: 1rem;
        .header + .content {
          padding-top: 0 !important;
        }
        p {
          padding-bottom: 1rem;
          margin-bottom: 0;
        }
        a {
          color: #d0d0d0;
          text-decoration: underline;

          &:hover {
            color: #e0e0e0;
            text-decoration: underline;
          }
        }
        ul {
          list-style: disc;
          margin-left: 2rem;
        }
      `}
      on='click'
      closeOnEscape
      position='top center'
      hoverable
      inverted
      wide={!small}
      header=''
      content={
        <Button.rounded theme={etheme.controls.button.confirm} onClick={onClick}>
          Confirm
        </Button.rounded>
      }
      {...props}
    />
  );
};

export const InfoBox: React.FC<{
  className?: string;
  iconClassName?: string;
  href?: string;
  red?: boolean;
  warning?: boolean;
  small?: boolean;
  content?: string | JSX.Element;
  children?: React.ReactNode;
}> = ({ className, iconClassName, href, small, red, warning, content, children }) => {
  return (
    <div
      className={`flex items-center ${className}`}
      css={[
        small ? tw`py-3/4e` : tw`py-1_25e`,
        tw`pr-1e  rounded-lg`,
        red ? tw`bg-coral-1 text-white` : tw`bg-blue-1`,
        warning ? tw`bg-red-3 text-gray-0` : null,
        href && red ? tw`hover:bg-red-2 cursor-pointer` : null,
        href && warning ? tw`hover:bg-red-3 cursor-pointer` : null,
        href && !red && !warning ? tw`hover:bg-blue-200 cursor-pointer` : null,
      ]}
      onClick={(e: SyntheticEvent): void => {
        if (href) {
          e.stopPropagation();
          window.open(href, '_blank');
        }
      }}
    >
      <InfoIcon
        className={iconClassName}
        css={[
          small ? tw`h-1_5e w-3e` : tw`h-3e w-6e`,
          tw`mx-1e`,
          red ? tw`text-gray-200` : warning ? tw`text-coral-1` : tw`text-color-brand`,
        ]}
      />
      <div>
        {content}
        {children}
      </div>
    </div>
  );
};
